// What a list read to its end says when one of its later pages failed (#1259).
//
// `useDrained` keeps the rows it had and reports the failed page's Problem as
// `incomplete`. This is the one way that is said, beside the rows, so a short
// list never stands on a page as though it were the whole one: the Problem,
// how many were read, and that the rest were not.
import type { Problem } from "../../api/problem";
import { ProblemBox } from "../../ui";

/** The nouns these lists count, in the plural their callers pass and the
 * singular one row takes. */
export type Noun = "devices" | "access points" | "switches" | "sites";
const ONE: Record<Noun, string> = { devices: "device", "access points": "access point", switches: "switch", sites: "site" };

/** The sentence alone, for a place that says it inline — a count, a picker. */
export function incompleteWords(read: number, noun: Noun): string {
  return `This list is incomplete: ${read} ${read === 1 ? ONE[noun] : noun} loaded before a later page failed. The rest did not load.`;
}

export default function Incomplete({ problem, read, noun, what, onRetry }: {
  problem?: Problem; read: number; noun: Noun;
  /** What the shortfall costs where the list is not a table — a drawing, a count. */
  what?: string;
  onRetry?: () => void;
}) {
  if (!problem) return null;
  return (
    <div>
      <ProblemBox problem={problem} />
      <p className="muted" role="status">
        {incompleteWords(read, noun)}{what ? ` ${what}` : ""}{" "}
        {onRetry && <button type="button" className="btn btn-quiet" onClick={onRetry}>Load again</button>}
      </p>
    </div>
  );
}

/** The one-line form, for a read that feeds a picker, a link's far end or a
 * drawing rather than a table of its own (`useSiteDevices`): the rows it holds
 * are offered, and this says they are not every one. */
export function IncompleteNote({ q, noun, what }: { q: { data?: { items: unknown[]; incomplete?: Problem } }; noun: Noun; what?: string }) {
  const p = q.data?.incomplete;
  if (!p) return null;
  return (
    <p className="muted" role="status">
      {incompleteWords(q.data!.items.length, noun)}{what ? ` ${what}` : ""} ({p.message})
    </p>
  );
}
